import { Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { ServiceUserTeam } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { SectionCard } from "@/components/ui/section-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAddServiceUserTeam,
  useRemoveServiceUserTeam,
  useServiceUserTeams,
} from "@/features/service-users/hooks/useServiceUserTeams";
import { useTeams } from "@/features/teams/hooks/useTeams";

export function ServiceUserTeamsPanel({
  serviceUserId,
}: {
  serviceUserId: string;
}) {
  const { t } = useTranslation("serviceUsers");
  const teamsQuery = useServiceUserTeams(serviceUserId);
  const allTeams = useTeams();
  const addTeam = useAddServiceUserTeam(serviceUserId);
  const [selected, setSelected] = useState("");

  const current = teamsQuery.data ?? [];
  const currentIds = new Set(current.map((m) => m.team_id));
  const available = (allTeams.data ?? []).filter((t) => !currentIds.has(t.id));

  async function handleAdd() {
    if (!selected) return;
    try {
      await addTeam.mutateAsync({ team_id: selected, role: "user" });
      setSelected("");
      toast.success(t("toasts.addedToTeam"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <SectionCard title={t("teams.title")} description={t("teams.description")}>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder={t("teams.selectTeam")} />
          </SelectTrigger>
          <SelectContent>
            {available.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={handleAdd} disabled={!selected || addTeam.isPending}>
          <Plus className="h-4 w-4" />
          {t("teams.add")}
        </Button>
      </div>

      {current.length === 0 ? (
        <p className="text-sm text-ink-500">{t("teams.empty")}</p>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line">
          {current.map((membership) => (
            <TeamRow
              key={membership.team_id}
              serviceUserId={serviceUserId}
              membership={membership}
            />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function TeamRow({
  serviceUserId,
  membership,
}: {
  serviceUserId: string;
  membership: ServiceUserTeam;
}) {
  const { t } = useTranslation("serviceUsers");
  const remove = useRemoveServiceUserTeam(serviceUserId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleRemove() {
    try {
      await remove.mutateAsync(membership.team_id);
      toast.success(t("toasts.removedFromTeam"));
      setConfirmOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <span className="flex items-center gap-2 font-medium text-ink-950">
        <Users className="h-4 w-4 text-ink-500" />
        {membership.team_name}
        <Badge variant="secondary">
          {t(`teams.roles.${membership.role}`)}
        </Badge>
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setConfirmOpen(true)}
        disabled={remove.isPending}
      >
        <Trash2 className="h-4 w-4" />
        {t("teams.remove")}
      </Button>

      <ConfirmDestructiveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("teams.removeConfirm.title")}
        description={t("teams.removeConfirm.description")}
        resourceName={membership.team_name}
        resourceKind={t("teams.removeConfirm.resourceKind")}
        confirmLabel={t("teams.removeConfirm.confirm")}
        onConfirm={handleRemove}
        isPending={remove.isPending}
      />
    </li>
  );
}
